// Sawyer 12 incident-wording-pet-notes (F3) — one shared, in-memory copy of the
// tenant's words for every mounted surface (nav, incident pages, in-heat
// dialog, dog profile History, the Settings card).
//
//   - Fetched once per (apiUrl, signed-in staff id). A different staffer on the
//     same tab (a shared desk tablet) is a different key: the old view is
//     dropped and never reused (AC-R2). No session ⇒ the entry is cleared.
//   - While loading, unavailable or on a pre-deploy server, every surface gets
//     the DEFAULT nouns immediately: the read never blocks a page (AC22) and an
//     older server is never guessed at (AC16).
//   - `setTenantVocabulary(apiUrl, view)` after the owner's Save updates every
//     mounted surface without a reload (AC10).
//   - No sessionStorage cache on purpose: it would outlive a word changed on
//     another device.
import { useEffect, useMemo, useSyncExternalStore } from 'react';
import { readSession } from './api.js';
import { DEFAULT_INCIDENT_NOUNS, incidentNouns, type IncidentNouns, type VocabularySource } from './tenant-vocabulary.js';
import { fetchTenantVocabulary, type TenantVocabularyView } from './tenant-vocabulary-api.js';

export type TenantVocabularyStatus = 'loading' | 'ready' | 'unavailable' | 'not_deployed';

interface Entry {
  key: string;
  status: TenantVocabularyStatus;
  view: TenantVocabularyView | null;
}

let entry: Entry | null = null;
let seq = 0;
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): Entry | null {
  return entry;
}

function sessionFor(apiUrl: string): { key: string; token: string } | null {
  try {
    const { token, staff } = readSession();
    if (!token || !staff?.id) return null;
    return { key: `${apiUrl}|${staff.id}`, token };
  } catch {
    return null;
  }
}

function loadTenantVocabulary(apiUrl: string, force = false): void {
  const s = sessionFor(apiUrl);
  if (!s) {
    if (entry !== null) {
      entry = null;
      seq++;
      emit();
    }
    return;
  }
  if (!force && entry?.key === s.key) return; // cached or already loading
  const mine = ++seq;
  entry = { key: s.key, status: 'loading', view: null };
  emit();
  void fetchTenantVocabulary(apiUrl, s.token).then((r) => {
    if (mine !== seq || entry?.key !== s.key) return; // a newer load or another staffer won
    entry =
      r.kind === 'ok'
        ? { key: s.key, status: 'ready', view: r.view }
        : { key: s.key, status: r.kind === 'not_deployed' ? 'not_deployed' : 'unavailable', view: null };
    emit();
  });
}

/** After the owner's Save: every mounted surface re-renders with the new words. */
export function setTenantVocabulary(apiUrl: string, view: TenantVocabularyView): void {
  const s = sessionFor(apiUrl);
  if (!s) return;
  seq++;
  entry = { key: s.key, status: 'ready', view };
  emit();
}

/** Tests only: the store is module-level, so vitest cases must not leak into each other. */
export function resetTenantVocabularyForTests(): void {
  entry = null;
  seq++;
  emit();
}

export interface TenantVocabularyState {
  incident: IncidentNouns;
  source: VocabularySource;
  status: TenantVocabularyStatus;
  view: TenantVocabularyView | null;
  reload: () => void;
}

export function useTenantVocabulary(apiUrl: string): TenantVocabularyState {
  const snap = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const key = sessionFor(apiUrl)?.key ?? null;
  useEffect(() => {
    loadTenantVocabulary(apiUrl);
  }, [apiUrl, key]);
  const current = snap !== null && snap.key === key ? snap : null;
  const view = current?.status === 'ready' ? current.view : null;
  const incident = useMemo(
    () => (view ? incidentNouns(view.incidentLabel, view.source.incidentLabel) : DEFAULT_INCIDENT_NOUNS),
    [view],
  );
  return {
    incident,
    source: incident.source,
    status: key === null ? 'unavailable' : (current?.status ?? 'loading'),
    view,
    reload: () => loadTenantVocabulary(apiUrl, true),
  };
}

/** The i18n key whose nav label follows the owner's word. */
export const INCIDENTS_NAV_KEY = 'admin.nav.incidents';

/**
 * Wraps AppShell's `t` so the Incidents nav item and page title read the
 * owner's word when one is configured. Every other key (and every call while
 * the word is the default) goes to `baseT` untouched, options included, so
 * Spanish keeps "Incidentes" until an owner types a word.
 *
 * The ONE cast: i18next's `TFunction` is a branded overloaded type that a
 * plain wrapper cannot satisfy structurally. The wrapper only intercepts one
 * key and delegates every call verbatim, so it is substitutable at runtime.
 */
export function useVocabularyT<T extends (...args: never[]) => unknown>(baseT: T, apiUrl: string): T {
  const { incident } = useTenantVocabulary(apiUrl);
  return useMemo(() => {
    if (incident.source !== 'configured') return baseT;
    const delegate = baseT as unknown as (...args: unknown[]) => unknown;
    const wrapped = (key: unknown, ...rest: unknown[]): unknown =>
      key === INCIDENTS_NAV_KEY ? incident.many : delegate(key, ...rest);
    return wrapped as unknown as T;
  }, [baseT, incident]);
}
